import React from 'react';
import { Link } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

function Avatar({ name }) {
  const initials = name?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?';
  return (
    <div className="shrink-0 w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full flex items-center justify-center text-base font-semibold">
      {initials}
    </div>
  );
}

function pct(course) {
  const raw = course.progress_rate ?? course.progress_percentage ?? course.completion_percentage ?? 0;
  const n = Number(raw) || 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function courseTitle(course) {
  return course.title || course.course_name || course.course_title || 'Untitled course';
}

function courseStatus(course) {
  const status = (course.status || '').toString().toLowerCase();
  const p = pct(course);
  if (status === 'completed' || p >= 100) return 'completed';
  if (status === 'not_started' || p === 0) return 'not_started';
  return 'in_progress';
}

const STATUS_META = {
  completed: {
    label: 'Completed',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    bar: 'bg-emerald-500',
  },
  in_progress: {
    label: 'In Progress',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    bar: 'bg-indigo-500',
  },
  not_started: {
    label: 'Not Started',
    badge: 'bg-gray-100 text-gray-600 border-gray-200',
    bar: 'bg-gray-300',
  },
};

function StatTile({ label, value, accent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</p>
      <p className={`mt-1 text-3xl font-bold ${accent || 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

function CourseRow({ course }) {
  const p = pct(course);
  const status = courseStatus(course);
  const meta = STATUS_META[status];
  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-medium text-gray-900 leading-snug">{courseTitle(course)}</h3>
        <span className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${meta.badge}`}>
          {meta.label}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
          <div className={`h-2 rounded-full transition-all ${meta.bar}`} style={{ width: `${p}%` }} />
        </div>
        <span className="text-sm font-semibold text-gray-700 tabular-nums w-12 text-right">{p}%</span>
      </div>
    </div>
  );
}

export default function UserProgress({ facility, user, progress = [] }) {
  const total = progress.length;
  const completed = progress.filter((c) => courseStatus(c) === 'completed').length;
  const inProgress = progress.filter((c) => courseStatus(c) === 'in_progress').length;
  const notStarted = progress.filter((c) => courseStatus(c) === 'not_started').length;
  const avg = total > 0 ? Math.round(progress.reduce((s, c) => s + pct(c), 0) / total) : 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Course Progress</h1>
          <p className="mt-1 text-sm text-gray-500">{facility.name}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-4">
            <Avatar name={user.display_name} />
            <div className="min-w-0">
              <p className="text-lg font-semibold text-gray-900 truncate">{user.display_name}</p>
              <p className="text-sm text-gray-500 truncate">{user.email}</p>
            </div>
          </div>
        </div>

        {total > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatTile label="Courses" value={total} />
            <StatTile label="Completed" value={completed} accent="text-emerald-600" />
            <StatTile label="In Progress" value={inProgress} accent="text-indigo-600" />
            <StatTile label="Avg Progress" value={`${avg}%`} />
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Courses</h2>
            {total > 0 && (
              <span className="ml-auto text-sm text-gray-400">
                {total} {total === 1 ? 'course' : 'courses'}
              </span>
            )}
          </div>

          {total === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-gray-600">No course progress data available yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {progress.map((course, i) => (
                <CourseRow key={course.course_id || course.id || i} course={course} />
              ))}
            </div>
          )}
        </div>

        {notStarted > 0 && total > 0 && (
          <p className="text-xs text-gray-400 text-center">
            {notStarted} course{notStarted === 1 ? '' : 's'} not yet started
          </p>
        )}
      </div>
    </Layout>
  );
}
